import React, { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import Navbar from "components/navbar";
import { useTranslation } from "react-i18next";
import Loader from "components/loader/loader";
import { errorToast, successToast } from "utils/commons";
import {
    getDisputes,
    getDispute,
    updateDispute,
    createDisputeMessage,
    getDisputeMessages,
} from "services/customAPI";
import {
    MdGavel,
    MdSend,
    MdClose,
    MdPerson,
    MdDirectionsCar,
    MdAdminPanelSettings,
    MdChat,
} from "react-icons/md";

interface Dispute {
    id: number;
    openedBy: string;
    openedByUserId: number;
    status: string;
    reason: string;
    resolution: string;
    createdAt: string;
    resolvedAt: string;
    ride: {
        id: number;
        pickUpAddress: string;
        dropAddress: string;
        fare: number;
        rider?: { firstName: string; lastName: string };
        driver?: { firstName: string; lastName: string };
    };
}

interface Message {
    id: number;
    senderType: string;
    senderId: number;
    message: string;
    visibleTo: string;
    createdAt: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    open: { label: "Aberta", color: "bg-amber-500/20 text-amber-400" },
    in_review: { label: "Em Análise", color: "bg-blue-500/20 text-blue-400" },
    resolved: { label: "Resolvida", color: "bg-green-500/20 text-green-400" },
    closed: { label: "Fechada", color: "bg-gray-500/20 text-gray-400" },
};

export default function DisputesPage() {
    const { t } = useTranslation();
    const [disputes, setDisputes] = useState<Dispute[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [selected, setSelected] = useState<Dispute | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMsg, setNewMsg] = useState("");
    const [msgTarget, setMsgTarget] = useState<"rider" | "driver" | "all">("all");
    const [resolution, setResolution] = useState("");

    const fetchDisputes = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getDisputes({ status: statusFilter || undefined });
            const data = Array.isArray(res?.data)
                ? res.data
                : Array.isArray(res?.data?.data)
                    ? res.data.data
                    : [];
            setDisputes(data);
        } catch {
            errorToast("Erro ao carregar disputas");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

    // Polling logic to refresh messages in real-time
    useEffect(() => {
        if (!selected?.id) return;

        const refreshMessages = async () => {
            try {
                const msgRes = await getDisputeMessages(selected.id);
                const messageData = Array.isArray(msgRes?.data)
                    ? msgRes.data
                    : Array.isArray(msgRes?.data?.data)
                        ? msgRes.data.data
                        : [];
                
                // Only update if the length changed to avoid unnecessary re-renders
                setMessages(prev => {
                    if (messageData.length !== prev.length) {
                        return messageData;
                    }
                    return prev;
                });
            } catch (err) {
                console.warn("Error polling messages:", err);
            }
        };

        const interval = setInterval(refreshMessages, 5000);
        return () => clearInterval(interval);
    }, [selected?.id]);

    const openDetail = async (d: Dispute) => {
        try {
            const res = await getDispute(d.id);
            const freshDispute = res?.data?.data ?? res?.data ?? null;
            setSelected(freshDispute);
            const msgRes = await getDisputeMessages(d.id);
            const messageData = Array.isArray(msgRes?.data)
                ? msgRes.data
                : Array.isArray(msgRes?.data?.data)
                    ? msgRes.data.data
                    : [];
            setMessages(messageData);
            setResolution(freshDispute?.resolution || "");
        } catch {
            errorToast("Erro ao abrir disputa");
        }
    };

    const sendMessage = async () => {
        if (!newMsg.trim() || !selected) return;
        try {
            await createDisputeMessage(selected.id, {
                senderType: "admin",
                senderId: 0,
                message: newMsg,
                visibleTo: msgTarget,
            });
            setNewMsg("");
            const msgRes = await getDisputeMessages(selected.id);
            const messageData = Array.isArray(msgRes?.data)
                ? msgRes.data
                : Array.isArray(msgRes?.data?.data)
                    ? msgRes.data.data
                    : [];
            setMessages(messageData);
            successToast("Mensagem enviada");
        } catch {
            errorToast("Erro ao enviar");
        }
    };

    const handleResolve = async (status: string) => {
        if (!selected) return;
        try {
            await updateDispute(selected.id, { status, resolution });
            successToast(`Disputa ${status === "resolved" ? "resolvida" : "fechada"}`);
            setSelected(null);
            fetchDisputes();
        } catch {
            errorToast("Erro ao atualizar");
        }
    };

    if (loading && disputes.length === 0) return (<><Navbar flag={false} brandText="Disputas" /><Loader /></>);

    return (
        <>
            <Navbar flag={false} brandText="Disputas" />
            <Card extra="w-full mt-4 p-6 h-full">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <MdGavel className="text-amber-400 text-2xl" />
                        <h2 className="text-xl font-bold text-white">Disputas</h2>
                        <span className="text-sm text-gray-400">({disputes.length})</span>
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 bg-navy-800 border border-white/10 rounded-xl text-white text-sm"
                    >
                        <option value="">Todos os Status</option>
                        <option value="open">Abertas</option>
                        <option value="in_review">Em Análise</option>
                        <option value="resolved">Resolvidas</option>
                        <option value="closed">Fechadas</option>
                    </select>
                </div>

                {/* Disputes list */}
                <div className="space-y-3">
                    {disputes.map((d) => (
                        <div
                            key={d.id}
                            onClick={() => openDetail(d)}
                            className="flex items-center justify-between p-4 bg-navy-800 rounded-xl border border-white/5 hover:border-amber-500/30 cursor-pointer transition-all"
                        >
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                    <span className="text-white font-semibold">#{d.id}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_LABELS[d.status]?.color}`}>
                                        {STATUS_LABELS[d.status]?.label}
                                    </span>
                                    <span className="text-gray-400 text-xs">
                                        Aberta por: {d.openedBy === "rider" ? "🧍 Passageiro" : "🚗 Motorista"}
                                    </span>
                                </div>
                                <p className="text-gray-400 text-sm">
                                    Corrida #{d.ride?.id} — {d.ride?.pickUpAddress} → {d.ride?.dropAddress}
                                </p>
                                {d.reason && <p className="text-gray-500 text-xs mt-1 truncate max-w-lg">{d.reason}</p>}
                            </div>
                            <div className="text-gray-500 text-xs">
                                {new Date(d.createdAt).toLocaleDateString("pt-BR")}
                            </div>
                        </div>
                    ))}
                    {disputes.length === 0 && (
                        <p className="text-center text-gray-500 py-8">Nenhuma disputa encontrada</p>
                    )}
                </div>

                {/* Detail modal */}
                {selected && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <Card extra="w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <MdGavel className="text-amber-400 text-xl" />
                                    <h3 className="text-lg font-bold text-white">Disputa #{selected.id}</h3>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_LABELS[selected.status]?.color}`}>
                                        {STATUS_LABELS[selected.status]?.label}
                                    </span>
                                </div>
                                <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-white">
                                    <MdClose size={24} />
                                </button>
                            </div>

                            {/* Ride info */}
                            <div className="bg-navy-900 rounded-xl p-4 mb-4">
                                <p className="text-white text-sm mb-1">
                                    <strong>Corrida #{selected.ride?.id}</strong> — R$ {selected.ride?.fare}
                                </p>
                                <p className="text-gray-400 text-xs">
                                    {selected.ride?.pickUpAddress} → {selected.ride?.dropAddress}
                                </p>
                                <div className="flex gap-4 mt-2 text-xs text-gray-400">
                                    <span>🧍 {selected.ride?.rider?.firstName} {selected.ride?.rider?.lastName}</span>
                                    <span>🚗 {selected.ride?.driver?.firstName} {selected.ride?.driver?.lastName}</span>
                                </div>
                            </div>

                            {/* Reason */}
                            {selected.reason && (
                                <div className="mb-4">
                                    <p className="text-xs text-gray-400 mb-1">Motivo:</p>
                                    <p className="text-white text-sm bg-navy-900 rounded-lg p-3">{selected.reason}</p>
                                </div>
                            )}

                            {/* Messages */}
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <MdChat className="text-cyan-400" />
                                    <h4 className="text-white font-semibold text-sm">Mensagens</h4>
                                </div>
                                <div className="space-y-2 max-h-60 overflow-y-auto bg-navy-900 rounded-xl p-3">
                                    {messages.map((m) => (
                                        <div key={m.id} className={`flex gap-2 ${m.senderType === "admin" ? "justify-end" : ""}`}>
                                            <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${m.senderType === "admin"
                                                ? "bg-cyan-500/20 text-cyan-300"
                                                : m.senderType === "rider"
                                                    ? "bg-purple-500/20 text-purple-300"
                                                    : "bg-amber-500/20 text-amber-300"
                                                }`}>
                                                <div className="flex items-center gap-1 text-xs opacity-70 mb-1">
                                                    {m.senderType === "admin" ? <MdAdminPanelSettings /> : m.senderType === "rider" ? <MdPerson /> : <MdDirectionsCar />}
                                                    {m.senderType === "admin" ? "Admin" : m.senderType === "rider" ? "Passageiro" : "Motorista"}
                                                    {m.visibleTo !== "all" && <span className="ml-1 italic">(privado para {m.visibleTo})</span>}
                                                </div>
                                                {m.message}
                                            </div>
                                        </div>
                                    ))}
                                    {messages.length === 0 && <p className="text-gray-500 text-center text-sm py-4">Nenhuma mensagem</p>}
                                </div>

                                {/* Send message */}
                                <div className="mt-3 flex gap-2">
                                    <select
                                        value={msgTarget}
                                        onChange={(e) => setMsgTarget(e.target.value as any)}
                                        className="px-2 py-2 bg-navy-800 border border-white/10 rounded-lg text-white text-xs w-28"
                                    >
                                        <option value="all">Para Todos</option>
                                        <option value="rider">Só Passageiro</option>
                                        <option value="driver">Só Motorista</option>
                                    </select>
                                    <input
                                        value={newMsg}
                                        onChange={(e) => setNewMsg(e.target.value)}
                                        placeholder="Escrever mensagem..."
                                        className="flex-1 px-3 py-2 bg-navy-800 border border-white/10 rounded-lg text-white text-sm"
                                        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                                    />
                                    <button onClick={sendMessage} className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors">
                                        <MdSend />
                                    </button>
                                </div>
                            </div>

                            {/* Resolution */}
                            {(selected.status === "open" || selected.status === "in_review") && (
                                <div className="border-t border-white/10 pt-4">
                                    <label className="text-sm text-gray-400 mb-1 block">Resolução</label>
                                    <textarea
                                        value={resolution}
                                        onChange={(e) => setResolution(e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 bg-navy-800 border border-white/10 rounded-lg text-white text-sm mb-3"
                                        placeholder="Descreva a resolução..."
                                    />
                                    <div className="flex gap-2">
                                        <button onClick={() => handleResolve("resolved")} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold">
                                            ✅ Resolver
                                        </button>
                                        <button onClick={() => handleResolve("closed")} className="px-4 py-2 bg-gray-600 text-white rounded-xl text-sm font-semibold">
                                            Fechar
                                        </button>
                                        {selected.status === "open" && (
                                            <button onClick={() => handleResolve("in_review")} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold">
                                                Em Análise
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Card>
                    </div>
                )}
            </Card>
        </>
    );
}
